import { useCountUp } from '../../hooks/useCountUp';

interface StatDefinition {
  readonly target: number;
  readonly suffix: string;
  readonly label: string;
  readonly displayOverride?: string;
}

const STATS: ReadonlyArray<StatDefinition> = [
  { target: 55, suffix: '', label: 'Metro Areas' },
  { target: 159, suffix: '', label: 'Springboard Occupations' },
  { target: 22, suffix: '', label: 'Occupational Groups' },
  { target: 30, suffix: '%', label: 'Share of Demand Met', displayOverride: '~30%' },
];

/** A single animated stat — counts up from 0 once it scrolls into view. */
function HeroStat({ stat, isLast }: { readonly stat: StatDefinition; readonly isLast: boolean }) {
  const [ref, value] = useCountUp({ target: stat.target });
  // Show "~30%" once the count completes; show "0", "1", … "30" while animating.
  const display = value === stat.target && stat.displayOverride ? stat.displayOverride : `${value}${stat.suffix}`;

  // A11y (WCAG 1.3.1 Info and Relationships): a <dl> may only directly contain
  // <dt>/<dd> or a PLAIN <div> wrapping one dt+dd group. The prior wrapper had
  // role="group" (making it a non-plain child → axe definition-list/dlitem
  // failures) and rendered <dd> before <dt>. We keep a plain div, put <dt>
  // (the term/label) first in source order, and reverse visually with
  // flex-col-reverse so the big number still sits on top. The dl/dt/dd
  // semantics now carry the "Metro Areas: 55" pairing to screen readers, so the
  // old role="group"+aria-label is no longer needed.
  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      // Prototype gives each stat a 1px right border in white/.12 to act as
      // an inter-stat divider, except the last one. See `_archive/home.html`
      // lines 219–223.
      className={`flex flex-col-reverse items-center gap-[6px] px-5 text-center ${
        isLast ? '' : 'md:border-r md:border-white/[0.12]'
      }`}
    >
      {/* CR-022 (size) + CR-023 (contrast), UIS review 2026-07-31: these
          hero stat tags were the first item UIS named — 8–10px (too small)
          and text-white/50 (too faint over the 83%-navy hero overlay).
          Raised to text-base (16px) and text-white/85 (matches the hero
          eyebrow pill, ≥4.5:1). All-caps kept — legible now at 16px. */}
      <dt className="text-base font-medium uppercase tracking-[0.08em] text-white/85">
        {stat.label}
        <span className="sr-only">: {stat.displayOverride ?? `${stat.target}${stat.suffix}`}</span>
      </dt>
      <dd
        aria-hidden="true"
        className="m-0 font-bold leading-none tracking-[-0.01em] text-white"
        style={{ fontSize: 'clamp(24px, 3.5vw, 36px)' }}
      >
        {display}
      </dd>
    </div>
  );
}

/** The horizontal row of four hero stats. */
export function HeroStats() {
  return (
    <dl className="grid w-full grid-cols-2 md:grid-cols-4">
      {STATS.map((stat, idx) => (
        <HeroStat key={stat.label} stat={stat} isLast={idx === STATS.length - 1} />
      ))}
    </dl>
  );
}
